import {
  useLocalBusinessConsent,
  storedBusinessRequest,
  persistBusinessRequest,
  isLocalConfirmationRequired,
  isDefiniteBusinessRejection,
} from '../../../hooks/useLocalBusinessConsent';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ManualOpRequest, ManualOpResult, OpManualConfiguration } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { errorMessage } from './errorMessage';

type Props = {
  memberId: string;
  enabledForNew?: boolean;
  configuration?: OpManualConfiguration;
  memberLocked?: boolean;
  queryUnavailable?: boolean;
};
export function ManualOpForm(props: Props) {
  const { memberId } = props;
  const identity = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      sessionVenueKey(s.accessToken),
      s.sessionRevision,
      s.allowedActions,
      memberId,
    ]),
  );
  return <ScopedManualOpForm key={identity} {...props} identity={identity} />;
}
function ScopedManualOpForm({
  memberId,
  identity,
  enabledForNew = false,
  configuration,
  memberLocked = false,
  queryUnavailable = false,
}: Props & { identity: string }) {
  const { t } = useTranslation('member');
  const localBusiness = useLocalBusinessConsent();
  const submitting = useRef(false);
  const amountInput = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const actor = useAuthStore((s) => s.user?.id);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const allowed = useAuthStore((s) => !!s.allowedActions?.includes('member:adjust_points'));
  const canManage = useAuthStore((s) => !!s.allowedActions?.includes('member:asset_type_manage'));
  const configurationKnown = !!configuration && configuration.targetVenueId === venue;
  const canStartNew =
    allowed &&
    enabledForNew &&
    configurationKnown &&
    configuration.configuredForNew &&
    !memberLocked &&
    !queryUnavailable;
  const storageKey = `member-manual-op:v1:${actor}:${venue}:${memberId}`;
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const current = () => {
    const state = useAuthStore.getState();
    return (
      live.current &&
      identity ===
        JSON.stringify([
          state.user?.id,
          sessionVenueKey(state.accessToken),
          state.sessionRevision,
          state.allowedActions,
          memberId,
        ])
    );
  };
  const [recoveryUnreadable] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return false;
      const value = storedBusinessRequest<ManualOpRequest>(storageKey)?.body;
      return !(
        value &&
        /^[0-9a-f-]{36}$/i.test(value.operationKey) &&
        ['GRANT', 'DEDUCT'].includes(value.direction) &&
        typeof value.amount === 'string' &&
        typeof value.note === 'string'
      );
    } catch {
      return true;
    }
  });
  const [pending, setPending] = useState<ManualOpRequest | null>(() => {
    try {
      const value = storedBusinessRequest<ManualOpRequest>(storageKey)?.body;
      return value &&
        /^[0-9a-f-]{36}$/i.test(value.operationKey) &&
        ['GRANT', 'DEDUCT'].includes(value.direction) &&
        typeof value.amount === 'string' &&
        typeof value.note === 'string'
        ? value
        : null;
    } catch {
      return null;
    }
  });
  const [direction, setDirection] = useState<'GRANT' | 'DEDUCT'>(pending?.direction ?? 'GRANT');
  const [amount, setAmount] = useState(pending?.amount ?? '');
  const [note, setNote] = useState(pending?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [message, setMessage] = useState('');
  const [receipt, setReceipt] = useState<ManualOpResult | null>(null);
  useEffect(() => {
    // A successful receipt clears the amount, disabling Save. Focus the next usable field.
    if (!busy && receipt && live.current) amountInput.current?.focus();
  }, [busy, receipt]);
  async function submit() {
    if (
      !allowed ||
      submitting.current ||
      busy ||
      !current() ||
      recoveryUnreadable ||
      (!canStartNew && !pending)
    )
      return;
    setReceipt(null);
    submitting.current = true;
    setBusy(true);
    let body = pending ?? {
      direction,
      amount,
      note: note.trim(),
      operationKey: crypto.randomUUID(),
    };
    let modern = !pending;
    try {
      const stored = storedBusinessRequest<ManualOpRequest>(storageKey);
      modern = !pending || !!stored?.modern;
      if (!pending || stored?.amendable) {
        const consent = await localBusiness.ask([memberId], 'OP');
        if (!consent || !current()) {
          submitting.current = false;
          setBusy(false);
          return;
        }
        body = { ...body, localInitializationConsent: consent };
      }
      if (modern) persistBusinessRequest(storageKey, body);
      else sessionStorage.setItem(storageKey, JSON.stringify(body));
    } catch (error) {
      if (current()) {
        setMessage(errorMessage(error, t('localBusiness.blocked')));
        setBusy(false);
      }
      submitting.current = false;
      return;
    }
    setPending(body);
    setBusy(true);
    setRejected(false);
    setMessage('');
    try {
      const result = await api.post<ManualOpResult>(`/members/${memberId}/op/manual`, body);
      if (!current()) return;
      if (
        result.operationKey !== body.operationKey ||
        result.actorId !== actor ||
        result.delta !== `${body.direction === 'DEDUCT' ? '-' : ''}${body.amount}`
      ) {
        setMessage(t('manualOp.uncertain'));
        return;
      }
      sessionStorage.removeItem(storageKey);
      setPending(null);
      setReceipt(result);
      setAmount('');
      setNote('');
      await Promise.all(
        ['member-ledger', 'member-ledger-summary', 'daily-ledgers'].map((key) =>
          qc.invalidateQueries({ queryKey: key === 'daily-ledgers' ? [key] : [key, memberId] }),
        ),
      );
    } catch (error) {
      if (!current()) return;
      if (isLocalConfirmationRequired(error)) {
        if (modern) persistBusinessRequest(storageKey, body, 'KNOWN_REJECTED');
        setMessage(t(modern ? 'localBusiness.reconfirm' : 'localBusiness.legacy'));
        return;
      }
      setRejected(!pending && isDefiniteBusinessRejection(error, 'OP'));
      setMessage(errorMessage(error, t('manualOp.uncertain')));
    } finally {
      submitting.current = false;
      if (current()) setBusy(false);
    }
  }
  if (!allowed) return <p role="status">{t('manualOp.noAuthority')}</p>;
  return (
    <section
      className="rounded-lg border border-base-300 p-4 space-y-3"
      aria-label={t('manualOp.title')}
    >
      {localBusiness.confirmation}
      {!canStartNew && (
        <div role="status" className="space-y-2">
          <p>{t('manualOp.unavailable')}</p>
          {(!configurationKnown || queryUnavailable) && !memberLocked && (
            <p>{t('manualOp.reloadRequired')}</p>
          )}
          {configurationKnown &&
            configuration.blockers.map((reason) => (
              <p key={reason}>{t(`manualOp.blockers.${reason}`)}</p>
            ))}
          {memberLocked && <p>{t('memberBalancesLocked')}</p>}
          {canManage && (
            <a className="link inline-flex min-h-11 items-center" href="/admin/points-config">
              {t('manualOp.settings')}
            </a>
          )}
          {!memberLocked && (
            <button
              type="button"
              className="min-h-11 border border-base-300 rounded px-3"
              onClick={() => {
                void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
                void qc.invalidateQueries({ queryKey: ['member-ledger', memberId] });
              }}
            >
              {t('manualOp.reload')}
            </button>
          )}
        </div>
      )}
      {(canStartNew || pending || recoveryUnreadable) && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          className="space-y-3"
        >
          <fieldset disabled={busy || !!pending} className="space-y-3">
            <legend className="sr-only">{t('ledgerOperation')}</legend>
            <div className="flex gap-4">
              {(['GRANT', 'DEDUCT'] as const).map((value) => (
                <label key={value} className="min-h-11 flex gap-2 items-center">
                  <input
                    type="radio"
                    name="manual-op-direction"
                    checked={direction === value}
                    onChange={() => setDirection(value)}
                  />
                  {t(value === 'GRANT' ? 'ledgerGrant' : 'ledgerDeduct')}
                </label>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                {t('ledgerAmountPoints')}
                <input
                  className="block w-full min-h-11 rounded border border-base-300 bg-base-100 px-3"
                  inputMode="numeric"
                  ref={amountInput}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
              <label className="block">
                {t('ledgerNote')}
                <input
                  className="block w-full min-h-11 rounded border border-base-300 bg-base-100 px-3"
                  maxLength={255}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </div>
          </fieldset>
          <button
            className="min-h-11 rounded bg-primary text-primary-content px-4"
            type="submit"
            disabled={
              busy ||
              recoveryUnreadable ||
              !/^[1-9]\d{0,9}$/.test(amount) ||
              Number(amount) > 2147483647
            }
          >
            {t(pending ? 'manualOp.retry' : 'ledgerSave')}
          </button>
          {pending && (
            <p role="status" className="break-all">
              {t('manualOp.pending', { key: pending.operationKey })}
            </p>
          )}
          {rejected && (
            <button
              type="button"
              className="min-h-11 border border-base-300 rounded px-3"
              onClick={() => {
                try {
                  sessionStorage.removeItem(storageKey);
                } catch {
                  return;
                }
                setPending(null);
                setRejected(false);
                setMessage('');
              }}
            >
              {t('manualOp.editRejected')}
            </button>
          )}
        </form>
      )}
      {recoveryUnreadable && <p role="alert">{t('manualOp.storageUnavailable')}</p>}
      {message && <p role="alert">{message}</p>}
      {receipt && <p role="status">{t('manualOp.saved', { amount: receipt.delta })}</p>}
    </section>
  );
}
